import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCw, ShieldCheck, Clock, Mail } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

function Card({ children, className = '' }) {
  return (
    <section className={`bg-[#13153A]/80 backdrop-blur-md border border-purple-500/20 rounded-2xl p-6 sm:p-8 ${className}`}>
      {children}
    </section>
  );
}

export default function RefundPage() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0D2B] via-[#13153A] to-[#0B0D2B] text-white" data-testid="refund-page">
      <Navbar />
      <div className="relative z-10 max-w-4xl mx-auto px-6 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30">
              <RefreshCw size={22} className="text-fuchsia-200" />
            </div>
            <div>
              <h1 className="font-syne text-3xl sm:text-4xl font-black tracking-tight" data-testid="refund-title">Refund & Cancellation Policy</h1>
              <p className="text-zinc-400 text-sm mt-1">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            </div>
          </div>

          <p className="text-zinc-300 text-base leading-relaxed max-w-3xl mb-8">
            EntrepreNatives is a marketplace platform. Refunds are issued for legitimate, lawful transactions
            in line with the rules below. Refunds are processed through <strong className="text-white">Stripe</strong>,
            our payment processor, back to the original card used at checkout.
          </p>

          <div className="space-y-5">
            <Card>
              <h2 className="font-syne text-xl font-bold mb-3 flex items-center gap-2"><Clock size={16} className="text-fuchsia-300" /> 1. Marketplace orders (services & products)</h2>
              <ul className="list-disc pl-6 text-zinc-300 space-y-2 text-sm">
                <li><strong className="text-white">Before work begins:</strong> Full refund (minus card processing fees) within 48 hours of booking if the vendor has not yet started work.</li>
                <li><strong className="text-white">After work has started:</strong> Partial refund determined through our dispute resolution process, based on the amount of work actually completed.</li>
                <li><strong className="text-white">Physical products:</strong> If a product arrives damaged, defective, or materially different from what was listed, contact us within 7 days of delivery for a refund or replacement. Vendors are responsible for return logistics on physical items.</li>
                <li><strong className="text-white">After delivery notification (digital services):</strong> The buyer has 7 days to request a revision or open a dispute. After 7 days, payment is automatically released to the vendor.</li>
                <li><strong className="text-white">After final payment release:</strong> Refund claims are reviewed case-by-case and may require arbitration.</li>
              </ul>
            </Card>

            <Card>
              <h2 className="font-syne text-xl font-bold mb-3">2. Subscription refunds</h2>
              <ul className="list-disc pl-6 text-zinc-300 space-y-2 text-sm">
                <li><strong className="text-white">Monthly subscriptions:</strong> No partial refunds. Cancellation takes effect at the end of the current billing period.</li>
                <li><strong className="text-white">Annual subscriptions:</strong> Pro-rated refund available within the first 30 days.</li>
                <li><strong className="text-white">Free trials:</strong> No charges apply during the trial; nothing to refund.</li>
              </ul>
            </Card>

            <Card>
              <h2 className="font-syne text-xl font-bold mb-3">3. Website Builder refunds</h2>
              <p className="text-zinc-300 text-sm">
                Website Builder subscriptions follow the same rules as vendor subscriptions above. Domain
                registration fees are non-refundable once a domain has been registered. Third-party add-on
                charges are subject to each provider's refund policy.
              </p>
            </Card>

            <Card>
              <h2 className="font-syne text-xl font-bold mb-3">4. Tips</h2>
              <p className="text-zinc-300 text-sm">
                Tips to vendors are voluntary and final. EntrepreNatives takes 0% on tips — 100% goes to the
                vendor. If a tip was sent in error, contact us within 24 hours.
              </p>
            </Card>

            <Card>
              <h2 className="font-syne text-xl font-bold mb-3">5. Non-refundable items</h2>
              <ul className="list-disc pl-6 text-zinc-300 space-y-2 text-sm">
                <li>Domain registration fees</li>
                <li>Completed and approved deliveries past the 7-day dispute window</li>
                <li>Founding Vendor / Founding Non-Profit lifetime seat fees (lifetime benefits already conferred)</li>
                <li>Card processing fees collected by Stripe on cancelled transactions</li>
              </ul>
            </Card>

            <Card>
              <h2 className="font-syne text-xl font-bold mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-300" /> 6. How to request a refund</h2>
              <ol className="list-decimal pl-6 text-zinc-300 space-y-2 text-sm">
                <li>Open the order in your dashboard and click <strong className="text-white">Request Revision</strong> or <strong className="text-white">Open a Dispute</strong>.</li>
                <li>If the order is past the in-app dispute window, email <a className="text-fuchsia-300 underline" href="mailto:support@entreprenatives.com">support@entreprenatives.com</a> with your order ID, the reason for the refund, and any supporting evidence (screenshots, files, etc.).</li>
                <li>Our team will respond within 1 business day. Approved refunds are processed by Stripe and typically post within 5–10 business days, depending on the issuing bank.</li>
              </ol>
            </Card>

            <Card>
              <h2 className="font-syne text-xl font-bold mb-3 flex items-center gap-2"><Mail size={16} className="text-fuchsia-300" /> 7. Contact</h2>
              <p className="text-zinc-300 text-sm">
                Questions about a refund or chargeback? Email
                <a className="text-fuchsia-300 underline mx-1" href="mailto:support@entreprenatives.com">support@entreprenatives.com</a>
                or visit our <Link to="/contact" className="text-fuchsia-300 underline">Contact page</Link>.
              </p>
            </Card>
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            <Link to="/terms" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Privacy Policy</Link>
            <Link to="/legal/dispute-resolution" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Dispute Resolution</Link>
            <Link to="/legal/payment-policy" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Payment Policy</Link>
            <Link to="/legal" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">All legal documents</Link>
          </div>

          <p className="text-zinc-500 text-xs mt-10 leading-relaxed max-w-3xl">
            EntrepreNatives is a marketplace platform connecting customers with independent Indigenous-owned
            businesses and service providers. We are not a financial institution. All payment processing and
            refunds are handled by Stripe, a licensed payment processor.
          </p>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
